import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Costs from "@/pages/Costs";
import Clients from "@/pages/Clients";
import Cashflow from "@/pages/Cashflow";
import Simulator from "@/pages/Simulator";
import ImportExport from "@/pages/ImportExport";
import BreakEven from "@/pages/BreakEven";
import FattureAttive from "@/pages/FattureAttive";
import FatturePassive from "@/pages/FatturePassive";
import Confronti from "@/pages/Confronti";
import Tasse from "@/pages/Tasse";
import Impostazioni from "@/pages/Impostazioni";
import ServiziFunerari from "@/pages/ServiziFunerari";
import RiconciliazioneBancaria from "@/pages/RiconciliazioneBancaria";
import EbitEbitda from "@/pages/EbitEbitda";
import ContiBancari from "@/pages/ContiBancari";
import DownloadApp from "@/pages/DownloadApp";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("treasury_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("treasury_token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("treasury_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("treasury_token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("treasury_token");
    setToken(null);
    setUser(null);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Layout with Sidebar
const AppLayout = ({ children }) => {
  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/costs"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Costs />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Clients />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cashflow"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Cashflow />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/simulator"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Simulator />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-export"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ImportExport />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/breakeven"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <BreakEven />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fatture-attive"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FattureAttive />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fatture-passive"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <FatturePassive />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/confronti"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Confronti />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasse"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Tasse />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/impostazioni"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Impostazioni />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/servizi-funerari"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ServiziFunerari />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/riconciliazione"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <RiconciliazioneBancaria />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ebit-ebitda"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <EbitEbitda />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/conti-bancari"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <ContiBancari />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/download"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DownloadApp />
                </AppLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
